import type { Dispatch } from '@reduxjs/toolkit';
import { Logger } from '@saviynt/common';

import {
  setAddAccessEntitlementTemplates,
  setAddAccessPrompt,
  setEntitlementSteps,
  setRemoveAccessPrompt,
  setSavedStepData,
  updateEntitlements,
  updateStepFormData,
} from '../../data-store/copilotSlice';

import { extractAccountInstructions } from './instructionUtils';
import type { TemplateAttribute } from './types';

interface GenericAttributeMapping {
  id: string;
  saviyntField: string;
  externalAppField: string;
  saviyntValue: string;
  externalAppValue: string;
  isMandatory: boolean;
}

interface ParsedOperationData {
  rawData: Record<string, unknown>;
  apiURL?: string;
  domainObjects?: string[];
  attributeDefaultValue?: string;
  mappings?: GenericAttributeMapping[];
  instructions?: string;
  attributeValue?: string | null;
}

const parseAccountStep = (
  attributes: TemplateAttribute[],
  isEditMode = false,
  dispatch?: Dispatch
): Record<string, ParsedOperationData> => {
  const result: Record<string, ParsedOperationData> = {};

  if (!isEditMode || !dispatch || !attributes) {
    return result;
  }

  attributes.forEach((attr) => {
    if (
      !attr ||
      !attr.displayOnUI ||
      !attr.attributeValue ||
      !attr.attributeName
    ) {
      return;
    }

    const stepName = mapAttributeToStepName(attr.attributeName);

    if (!stepName) {
      return;
    }

    try {
      const operationData = parseOperationData(attr.attributeValue, attr);

      result[stepName] = operationData;

      // Populate stepFormData for form pre-filling
      if (operationData.mappings && operationData.mappings.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        (dispatch as (action: unknown) => void)(
          updateStepFormData({
            stepName,
            formData: {
              mappings: operationData.mappings,
              instructions: operationData.instructions,
              ...operationData.rawData,
            },
          })
        );

        (dispatch as (action: unknown) => void)(
          setSavedStepData({
            stepName,
            formData: {
              mappings: operationData.mappings,
              instructions: operationData.instructions,
              ...operationData.rawData,
            },
          })
        );

        // Special handling for AddAccess in edit mode - initialize all data structures
        if (
          stepName === 'AddAccess' &&
          attr.attributeValue &&
          attr.attributeValue !== operationData.attributeDefaultValue
        ) {
          // Use the actual saved attributeValue (not the processed one)
          initializeAddAccessEditMode(attr.attributeValue, dispatch);
        }

        // Special handling for RemoveAccess in edit mode - initialize all data structures
        if (
          stepName === 'RemoveAccess' &&
          attr.attributeValue &&
          attr.attributeValue !== operationData.attributeDefaultValue
        ) {
          // Use the actual saved attributeValue (not the processed one)
          initializeRemoveAccessEditMode(attr.attributeValue, dispatch);
        }
      } else if (
        operationData.rawData &&
        Object.keys(operationData.rawData).length > 0
      ) {
        dispatch(
          updateStepFormData({ stepName, formData: operationData.rawData })
        );

        dispatch(
          setSavedStepData({ stepName, formData: operationData.rawData })
        );
      }
    } catch (error) {
      // Skip invalid JSON data
    }
  });

  return result;
};

const mapAttributeToStepName = (attributeName: string): string | null => {
  const mapping: Record<string, string> = {
    'externalconnattvalue.CreateAccountJSON': 'CreateAccount',
    'externalconnattvalue.UpdateAccountJSON': 'UpdateAccount',
    'externalconnattvalue.EnableAccountJSON': 'EnableAccount',
    'externalconnattvalue.DisableAccountJSON': 'DisableAccount',
    'externalconnattvalue.RemoveAccountJSON': 'RemoveAccount',
    'externalconnattvalue.AddAccessJSON': 'AddAccess',
    'externalconnattvalue.RemoveAccessJSON': 'RemoveAccess',
  };

  return mapping[attributeName] || null;
};

const parseOperationData = (
  attributeValue: string,
  attr: TemplateAttribute
): ParsedOperationData => {
  const jsonData: unknown = JSON.parse(attributeValue);

  if (!isValidJsonData(jsonData)) {
    return {
      rawData: {},
      apiURL: attr.apiURL || undefined,
      domainObjects: attr.domainObjects || undefined,
      attributeDefaultValue: attr.attributeDefaultValue || undefined,
      attributeValue: attr.attributeValue,
    };
  }

  const httpParams = jsonData.call[0]?.httpParams;

  if (typeof httpParams !== 'string') {
    return {
      rawData: {},
      apiURL: attr.apiURL || undefined,
      domainObjects: attr.domainObjects || undefined,
      attributeDefaultValue: attr.attributeDefaultValue || undefined,
      attributeValue: attr.attributeValue,
    };
  }

  // Fix the configInfo field by finding and properly escaping it
  let fixedHttpParams = httpParams;

  // Find configInfo field and extract its value
  const configInfoMatch = httpParams.match(/"configInfo":"(.*?)"}}/s);

  if (configInfoMatch) {
    const originalValue = configInfoMatch[1];
    const escapedValue = originalValue
      .replace(/\\"/g, '___TEMP_ESCAPED_QUOTE___') // Preserve already escaped quotes
      .replace(/"/g, '\\"') // Escape unescaped quotes
      .replace(/___TEMP_ESCAPED_QUOTE___/g, '\\"') // Restore escaped quotes
      .replace(/\n/g, '\\n') // Escape newlines
      .replace(/\r/g, '\\r') // Escape carriage returns
      .replace(/\t/g, '\\t'); // Escape tabs

    fixedHttpParams = httpParams.replace(
      /"configInfo":".*?"}}$/s,
      `"configInfo":"${escapedValue}"}}`
    );
  }

  const paramsData: unknown = JSON.parse(fixedHttpParams);
  const dataObject = isValidParamsData(paramsData) ? paramsData.data : {};

  if (!isValidDataObject(dataObject)) {
    return {
      rawData: {},
      apiURL: attr.apiURL || undefined,
      domainObjects: attr.domainObjects || undefined,
      attributeDefaultValue: attr.attributeDefaultValue || undefined,
      attributeValue: attr.attributeValue,
    };
  }

  // Extract mappings from data object (exclude configInfo)
  const mappings = extractMappingsFromData(dataObject);

  // Extract instructions from configInfo
  const instructions = extractAccountInstructions(
    dataObject.configInfo as string
  );

  return {
    rawData: dataObject,
    apiURL: attr.apiURL || undefined,
    domainObjects: attr.domainObjects || undefined,
    attributeDefaultValue: attr.attributeDefaultValue || undefined,
    mappings,
    instructions,
    attributeValue: attr.attributeValue,
  };
};

const extractMappingsFromData = (
  dataObject: Record<string, unknown>
): GenericAttributeMapping[] => {
  const mappings: GenericAttributeMapping[] = [];
  let index = 0;

  Object.entries(dataObject).forEach(([key, value]) => {
    // Skip configInfo and other non-mapping fields
    if (key === 'configInfo' || typeof value !== 'string') {
      return;
    }

    // Check if value contains Saviynt attribute pattern ${...}
    const saviyntMatch = value.match(/\$\{([^}]+)\}/);

    if (saviyntMatch) {
      const saviyntField = saviyntMatch[1]; // Extract content inside ${}

      mappings.push({
        id: `parsed-${index}`,
        saviyntField,
        externalAppField: key,
        saviyntValue: '',
        externalAppValue: '',
        isMandatory: false,
      });

      index += 1;
    }
  });

  return mappings;
};

const isValidJsonData = (
  data: unknown
): data is { call: { httpParams?: string }[] } =>
  typeof data === 'object' &&
  data !== null &&
  'call' in data &&
  Array.isArray((data as { call: unknown }).call) &&
  (data as { call: unknown[] }).call.length > 0;

const isValidParamsData = (data: unknown): data is { data: unknown } =>
  typeof data === 'object' && data !== null && 'data' in data;

const isValidDataObject = (data: unknown): data is Record<string, unknown> =>
  typeof data === 'object' && data !== null;

/**
 * Initialize AddAccess edit mode - set up all data structures like create mode
 */
const initializeAddAccessEditMode = (
  attributeValue: string,
  dispatch: Dispatch
) => {
  try {
    const savedData = JSON.parse(attributeValue) as { call?: unknown[] };

    if (!savedData.call || !Array.isArray(savedData.call)) {
      return;
    }

    const entitlements: { id: number; value: string }[] = [];
    const templates: unknown[] = [];

    // Extract entitlements, mappings, and prompts from saved data
    savedData.call.forEach((item: unknown, index: number) => {
      const entry = item as { name?: string; httpParams?: string };

      if (entry.name) {
        // Create entitlement
        entitlements.push({ id: Date.now() + index, value: entry.name });

        // Add to templates array
        templates.push(entry);

        // Extract mappings and prompt from httpParams
        const mappings: GenericAttributeMapping[] = [];
        let prompt = '';

        if (entry.httpParams) {
          try {
            // Fix the configInfo field by finding and properly escaping it (same as parseOperationData)
            let fixedHttpParams = entry.httpParams;

            // Find configInfo field and extract its value
            const configInfoMatch = entry.httpParams.match(
              /"configInfo":"(.*?)"}}/s
            );

            if (configInfoMatch) {
              const originalValue = configInfoMatch[1];
              const escapedValue = originalValue
                .replace(/\\"/g, '___TEMP_ESCAPED_QUOTE___') // Preserve already escaped quotes
                .replace(/"/g, '\\"') // Escape unescaped quotes
                .replace(/___TEMP_ESCAPED_QUOTE___/g, '\\"') // Restore escaped quotes
                .replace(/\n/g, '\\n') // Escape newlines
                .replace(/\r/g, '\\r') // Escape carriage returns
                .replace(/\t/g, '\\t'); // Escape tabs

              fixedHttpParams = entry.httpParams.replace(
                /"configInfo":".*?"}}$/s,
                `"configInfo":"${escapedValue}"}}`
              );
            }

            const httpParamsData = JSON.parse(fixedHttpParams) as {
              data?: Record<string, unknown>;
            };

            if (
              httpParamsData.data &&
              typeof httpParamsData.data === 'object'
            ) {
              // Use existing functions from create flow
              mappings.push(...extractMappingsFromData(httpParamsData.data));
              prompt = extractAccountInstructions(
                httpParamsData.data.configInfo as string
              );
            }
          } catch (e) {
            // If parsing fails, continue with empty mappings
            Logger.error(e);
          }
        }

        // Always create the step data regardless of parsing success
        dispatch(
          updateStepFormData({
            stepName: `AddAccess_${index}`,
            formData: { mappings: mappings || [], entitlementIndex: index },
          })
        );

        dispatch(
          setSavedStepData({
            stepName: `AddAccess_${index}`,
            formData: { mappings: mappings || [], entitlementIndex: index },
          })
        );

        // Store prompt if exists
        if (prompt && typeof setAddAccessPrompt === 'function') {
          dispatch(setAddAccessPrompt({ index, prompt }));
        }
      }
    });

    // Set up entitlements and iteration
    if (entitlements.length > 0) {
      dispatch(updateEntitlements(entitlements));
      dispatch(
        updateStepFormData({
          stepName: 'AddAccess',
          formData: { entitlements },
        })
      );
      dispatch(
        setSavedStepData({
          stepName: 'AddAccess',
          formData: { entitlements },
        })
      );

      // Set up entitlement steps for iteration
      dispatch(setEntitlementSteps(entitlements));

      // Store templates
      dispatch(setAddAccessEntitlementTemplates(templates));

      // Create the proper AddAccessConfirmation payload with all entitlements
      const confirmationPayload = {
        multipleconnection: false,
        'externalconnattvalue.AddAccessJSON': attributeValue, // Use the complete saved data
      };

      dispatch(
        updateStepFormData({
          stepName: 'AddAccessConfirmation',
          formData: confirmationPayload,
        })
      );

      dispatch(
        setSavedStepData({
          stepName: 'AddAccessConfirmation',
          formData: confirmationPayload,
        })
      );
    }
  } catch (e) {
    // Skip initialization if parsing fails
  }
};

/**
 * Initialize RemoveAccess edit mode - set up all data structures like create mode
 */
const initializeRemoveAccessEditMode = (
  attributeValue: string,
  dispatch: Dispatch
) => {
  try {
    const savedData = JSON.parse(attributeValue) as { call?: unknown[] };

    if (!savedData.call || !Array.isArray(savedData.call)) {
      return;
    }

    const entitlements: { id: number; value: string }[] = [];
    const templates: unknown[] = [];

    // Extract entitlements, mappings, and prompts from saved data
    savedData.call.forEach((item: unknown, index: number) => {
      const entry = item as { name?: string; httpParams?: string };

      if (entry.name) {
        // Create entitlement
        entitlements.push({ id: Date.now() + index, value: entry.name });

        // Add to templates array
        templates.push(entry);

        // Extract mappings and prompts by comparing with attributeDefaultValue
        const mappings: GenericAttributeMapping[] = [];
        let prompt = '';

        if (entry.httpParams) {
          try {
            // Fix the configInfo field by finding and properly escaping it (same as parseOperationData)
            let fixedHttpParams = entry.httpParams;

            // Find configInfo field and extract its value
            const configInfoMatch = entry.httpParams.match(
              /"configInfo":"(.*?)"}}/s
            );

            if (configInfoMatch) {
              const originalValue = configInfoMatch[1];
              const escapedValue = originalValue
                .replace(/\\"/g, '___TEMP_ESCAPED_QUOTE___') // Preserve already escaped quotes
                .replace(/"/g, '\\"') // Escape unescaped quotes
                .replace(/___TEMP_ESCAPED_QUOTE___/g, '\\"') // Restore escaped quotes
                .replace(/\n/g, '\\n') // Escape newlines
                .replace(/\r/g, '\\r') // Escape carriage returns
                .replace(/\t/g, '\\t'); // Escape tabs

              fixedHttpParams = entry.httpParams.replace(
                /"configInfo":".*?"}}$/s,
                `"configInfo":"${escapedValue}"}}`
              );
            }

            // Parse the httpParams to get the data section
            const savedParams = JSON.parse(fixedHttpParams) as {
              data?: Record<string, unknown>;
            };

            if (savedParams.data && typeof savedParams.data === 'object') {
              // Use existing functions from create flow
              mappings.push(...extractMappingsFromData(savedParams.data));
              prompt = extractAccountInstructions(
                savedParams.data.configInfo as string
              );
            }
          } catch (e) {
            // If parsing fails, continue with empty mappings
          }
        }

        // Always create the step data regardless of parsing success
        if (dispatch && typeof dispatch === 'function') {
          dispatch(
            updateStepFormData({
              stepName: `RemoveAccess_${index}`,
              formData: { mappings: mappings || [], entitlementIndex: index },
            })
          );

          dispatch(
            setSavedStepData({
              stepName: `RemoveAccess_${index}`,
              formData: { mappings: mappings || [], entitlementIndex: index },
            })
          );
        }

        // Store prompt if exists
        if (prompt && dispatch && typeof dispatch === 'function') {
          dispatch(setRemoveAccessPrompt({ index, prompt }));
        }
      }
    });
  } catch (e) {
    // Skip initialization if parsing fails
  }
};

export default parseAccountStep;
